import React, { useState, useMemo, useEffect, useRef } from 'react';
import { IoMoon, IoSunny, IoLogOutOutline, IoFlash, IoGift, IoSparkles, IoBookmark, IoCreateOutline } from "react-icons/io5";
import FileUploadArea from './FileUploadArea';
import GeneratedPoem from './GeneratedPoem';
import RecentPDFs from './RecentPDFs';
import KeywordSubscriptions from './KeywordSubscriptions';
import RecommendationSection from './RecommendationSection';
import DailyMeme from './DailyMeme';
import BlogSection from './BlogSection';
import usePDFMetadata from '../hooks/usePDFMetadata';
import useKeywords from '../hooks/useKeywords';
import TokenDisplay from './TokenDisplay';
import TokenPurchase from './TokenPurchase';
import TextPolishModal from './TextPolishModal';
import MessageBanner from './MessageBanner';
import ReferralModal from './ReferralModal';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import packageJson from '../../package.json';

const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const HomeTab = React.memo(({ 
  recentPdfs, 
  onOpenPdf, 
  onAddPdf, 
  onRemoveFromRecent, 
  isDarkMode, 
  toggleDarkMode, 
  userInfo, 
  onLogout,
  messageVisible,
  onMessageDismiss,
  dismissedMessages,
  isAuthenticated = true,
  onLogin
}) => {
  const sortedPdfs = usePDFMetadata(recentPdfs);
  const { keywords, recommendedPdfs, selectedKeyword, setSelectedKeyword, addKeyword, removeKeyword, checkAndUpdateKeyword, updateAllKeywords, loadingKeywords } = useKeywords();
  const { t, i18n } = useTranslation();

  const [showSubscriptionInput, setShowSubscriptionInput] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [showAllPapers, setShowAllPapers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMeme, setShowMeme] = useState(false);
  const [hasSeenMeme, setHasSeenMeme] = useState(() => {
    return localStorage.getItem('hasSeenMeme') === 'true';
  });
  const [showLogoutButton, setShowLogoutButton] = useState(false);
  const logoutButtonRef = useRef(null);
  const [showTokenPurchase, setShowTokenPurchase] = useState(false);
  const [tokenDisplayKey, setTokenDisplayKey] = useState(0);
  const [showPolishModal, setShowPolishModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Check if user is on an older version (before 0.2.9)
  const isOlderVersion = useMemo(() => {
    const currentVersion = packageJson.version.split('.').map(Number);
    const targetVersion = [0, 2, 9];
    
    for (let i = 0; i < currentVersion.length; i++) {
      if (currentVersion[i] < targetVersion[i]) return true;
      if (currentVersion[i] > targetVersion[i]) return false;
    }
    return false;
  }, []);
  
  // Custom system message for older versions
  const [systemMessages, setSystemMessages] = useState([]);
  
  useEffect(() => {
    if (keywords.length > 0 && !selectedKeyword) {
      setSelectedKeyword(keywords[0].keyword);
    }
  }, [keywords, selectedKeyword, setSelectedKeyword]);

  useEffect(() => {
    const checkFirstKeyword = async () => {
      if (keywords.length > 0) {
        const firstKeyword = keywords[0];
        if (Date.now() - firstKeyword.timestamp > CACHE_EXPIRATION) {
          await checkAndUpdateKeyword(firstKeyword.keyword);
        }
      }
    };

    checkFirstKeyword();
  }, [keywords, checkAndUpdateKeyword]);

  useEffect(() => {
    // Add update guidance message for older versions
    if (isOlderVersion) {
      setSystemMessages([{
        id: 'update-guidance',
        title: '重要更新提示',
        content: '您正在使用旧版本的应用程序。请更新到最新版本以获得更好的体验和新功能。更新后，请确保完全关闭应用程序并重新启动。',
        priority: 'high',
        dismissible: true
      }]);
    }
  }, [isOlderVersion]);

  const handleAddKeyword = async () => {
    setShowSubscriptionInput(false);
    if (newKeyword.trim()) {
      setIsLoading(true);
      await addKeyword(newKeyword.trim());
      setShowAllPapers(false);
      setIsLoading(false);
    }
    setNewKeyword('');
  };

  const handleRemoveKeyword = async (id, keyword) => {
    await removeKeyword(id, keyword);
  };

  const memoizedGeneratedPoem = useMemo(
    () => <GeneratedPoem key={i18n.language} isDarkMode={isDarkMode} />,
    [i18n.language, isDarkMode]
  );

  const handlePubMedClick = (PMID) => {
    window.open(`https://pubmed.ncbi.nlm.nih.gov/${PMID}/`, '_blank', 'noopener,noreferrer');
  };

  const toggleSubscriptionInput = (value) => {
    setShowSubscriptionInput(prev => value === undefined ? !prev : value);
  };

  const handleKeywordClick = async (keyword) => {
    await checkAndUpdateKeyword(keyword);
    setSelectedKeyword(keyword);
    setShowAllPapers(false);
  };

  const toggleShowAllPapers = async () => {
    setShowAllPapers(!showAllPapers);
    if (!showAllPapers) {
      setSelectedKeyword(null);
      // Update all keywords when "Show all" is clicked
      for (const keyword of keywords) {
        if (Date.now() - keyword.timestamp > CACHE_EXPIRATION) {
          await updateAllKeywords(keyword.keyword);
        }
      }
    } else if (keywords.length > 0) {
      setSelectedKeyword(keywords[0].keyword);
    }
  };

  const filteredPdfs = showAllPapers
    ? Object.values(recommendedPdfs).flat()
    : (selectedKeyword ? recommendedPdfs[selectedKeyword] || [] : []);

  const displayedPdfs = showAllPapers ? filteredPdfs : filteredPdfs.slice(0, 3);

  const displayedKeywords = useMemo(() => {
    return new Set(displayedPdfs.map(pdf => pdf.keyword));
  }, [displayedPdfs]);

  const handleShowMeme = () => {
    if (!showMeme) {
      setShowMeme(true);
      if (!hasSeenMeme) {
        setHasSeenMeme(true);
        localStorage.setItem('hasSeenMeme', 'true');
      }
    } else {
      setShowMeme(false);
    }
  };

  const toggleLogoutButton = () => {
    setShowLogoutButton(!showLogoutButton);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (logoutButtonRef.current && !logoutButtonRef.current.contains(event.target)) {
        setShowLogoutButton(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTokenPurchaseClose = () => {
    setShowTokenPurchase(false);
    setTokenDisplayKey(prev => prev + 1);
  };

  const handlePolishModalClose = () => {
    setShowPolishModal(false);
  };

  return (
    <div className={`relative z-0 h-[100dvh] w-full overflow-hidden ${isDarkMode ? 'bg-[#0d1117]' : 'bg-gray-100'}`}>
      {/* Show system messages if any */}
      {systemMessages.length > 0 && (
        <div className="mb-4">
          <MessageBanner
            isDarkMode={isDarkMode}
            isVisible={true}
            onDismiss={(id) => {
              setSystemMessages(systemMessages.filter(msg => msg.id !== id));
            }}
            dismissedMessages={{}}
            customMessages={systemMessages}
          />
        </div>
      )}
      
      {messageVisible && (
        <div className="mb-4">
          <MessageBanner
            isDarkMode={isDarkMode}
            isVisible={messageVisible}
            onDismiss={onMessageDismiss}
            dismissedMessages={dismissedMessages}
          />
        </div>
      )}
      
      {/* Enhanced blur effect background with animated gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute h-3/6 w-2/5 -left-1/4 top-0 opacity-70"
          animate={{ 
            rotate: [0, 2, 0, -2, 0],
            scale: [1, 1.05, 1, 0.95, 1]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        >
          <div className="h-full w-full opacity-70 blur-3xl">
            <div className="h-full w-full blur-3xl">
              <div className={`rotate-30 h-full w-full ${isDarkMode ? 'bg-[#21262d]' : 'bg-blue-500'} blur-3xl`} style={{ clipPath: 'polygon(40% 0%, 100% 30%, 80% 100%, 20% 100%, 0% 30%)' }}></div>
            </div>
          </div>
        </motion.div>
        <motion.div 
          className="absolute h-2/6 w-1/6 left-1/4 bottom-0 opacity-80"
          animate={{ 
            rotate: [0, -3, 0, 3, 0],
            scale: [1, 0.95, 1, 1.05, 1]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        >
          <div className="h-full w-full opacity-80 blur-3xl">
            <div className="h-full w-full blur-3xl">
              <div className={`rotate-45 h-full w-full ${isDarkMode ? 'bg-[#21262d]' : 'bg-blue-500'}`} style={{ clipPath: 'polygon(60% 0%, 100% 40%, 70% 100%, 30% 100%, 0% 40%)' }}></div>
            </div>
          </div>
        </motion.div>
        <motion.div 
          className="absolute h-3/6 w-2/5 right-0 top-1/4 opacity-60"
          animate={{ 
            rotate: [0, 3, 0, -3, 0],
            y: [0, 10, 0, -10, 0]
          }}
          transition={{ 
            duration: 18, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          <div className="h-full w-full opacity-60 blur-3xl">
            <div className="h-full w-full blur-3xl">
              <div className={`rotate-12 h-full w-full ${isDarkMode ? 'bg-[#21262d]' : 'bg-pink-500'}`} style={{ clipPath: 'polygon(50% 0%, 100% 20%, 80% 100%, 20% 100%, 0% 20%)' }}></div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className={`relative z-10 h-full p-3 md:p-4 ${isDarkMode ? 'bg-[#020817]/35' : 'bg-white/30'}`}>
        <div className="grid h-full min-h-0 gap-4 xl:grid-cols-[minmax(300px,340px)_minmax(0,1fr)]">
          <aside className="relative min-h-0">
            <motion.button
              onClick={toggleDarkMode}
              className={`absolute right-3 top-3 z-20 rounded-full border p-2.5 shadow-lg backdrop-blur xl:-right-3 ${
                isDarkMode
                  ? 'border-white/10 bg-[#07111f]/92 text-yellow-300'
                  : 'border-slate-200/90 bg-white/92 text-slate-700'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={isDarkMode ? t('common.lightMode') : t('common.darkMode')}
              aria-label={isDarkMode ? t('common.lightMode') : t('common.darkMode')}
            >
              {isDarkMode ? <IoSunny size={16} /> : <IoMoon size={16} />}
            </motion.button>

            <div className="flex h-full min-h-0 flex-col">
              <div className={`homepage-scrollbar homepage-scroll-fade min-h-0 flex-1 space-y-4 overflow-y-auto pr-2 ${
                isAuthenticated ? 'pb-6' : 'pb-28'
              }`}>
                {isAuthenticated && (
                  <section className={`rounded-[28px] border p-4 ${
                    isDarkMode ? 'border-white/8 bg-[#07111f]/82' : 'border-slate-200/90 bg-white/88'
                  }`}>
                    <div className="flex items-center gap-3">
                      <motion.img
                        src={userInfo?.headimgurl || `https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo?.nickname || userInfo?.email || 'User')}&background=random&color=fff&size=128`}
                        alt="User"
                        className="h-11 w-11 rounded-full border border-white/10"
                        whileHover={{ scale: 1.04 }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className={`truncate text-sm font-semibold ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          {(() => {
                            const name = userInfo?.nickname || userInfo?.email;
                            if (!name) return t('homeTab.researcher', 'Researcher');
                            // Fix garbled WeChat names: try to decode if it looks like mis-encoded UTF-8
                            try {
                              const decoded = decodeURIComponent(escape(name));
                              return decoded !== name ? decoded : name;
                            } catch {
                              return name;
                            }
                          })()}
                        </p>
                        <p className={`text-xs ${
                          isDarkMode ? 'text-slate-400' : 'text-slate-500'
                        }`}>
                          {t('homeTab.profileStatus', 'Personalized feed active')}
                        </p>
                      </div>
                      <div className="relative" ref={logoutButtonRef}>
                        <motion.button
                          onClick={toggleLogoutButton}
                          className={`rounded-full p-2 ${
                            isDarkMode ? 'bg-white/6 text-slate-200' : 'bg-slate-100 text-slate-700'
                          }`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <IoLogOutOutline size={16} />
                        </motion.button>
                        <AnimatePresence>
                          {showLogoutButton && (
                            <motion.button
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                              transition={{ duration: 0.2 }}
                              onClick={onLogout}
                              className={`absolute right-0 top-12 whitespace-nowrap rounded-full px-3 py-2 text-sm ${
                                isDarkMode ? 'bg-[#21262d] text-gray-200 hover:bg-[#30363d]' : 'bg-white text-gray-800 hover:bg-gray-100'
                              } shadow-lg`}
                            >
                              {t('homeTab.logout')}
                            </motion.button>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {!showPolishModal && (
                      <div className="mt-3">
                        <TokenDisplay key={tokenDisplayKey} />
                      </div>
                    )}

                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <motion.button
                        onClick={() => setShowTokenPurchase(true)}
                        className="btn-cyber-narrow bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                        whileHover={{ scale: 1.03, boxShadow: '0 0 15px rgba(234, 88, 12, 0.45)' }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <span className="flex items-center justify-center space-x-1">
                          <IoFlash className="text-base text-yellow-100" />
                          <span className="font-bold tracking-wider">Token Shop</span>
                        </span>
                      </motion.button>
                      <motion.button
                        onClick={() => setShowReferralModal(true)}
                        className="btn-cyber-narrow"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <span className="flex items-center justify-center space-x-1">
                          <IoGift className="text-base" />
                          <span className="font-bold tracking-wider">{t('homeTab.recommend')}</span>
                        </span>
                      </motion.button>
                    </div>
                  </section>
                )}

                <section className={`relative overflow-hidden rounded-[28px] border p-4 ${
                  isDarkMode ? 'border-white/8 bg-[#07111f]/82' : 'border-slate-200/90 bg-white/88'
                }`}>
                  <FileUploadArea onAddPdf={onAddPdf} isDarkMode={isDarkMode} compact />

                  <motion.button
                    onClick={() => setShowPolishModal(true)}
                    className={`mt-3 flex w-full items-center justify-center gap-3 rounded-[20px] border px-5 py-3.5 text-base font-semibold ${
                      isDarkMode
                        ? 'border-purple-700/30 bg-gradient-to-r from-purple-900/35 to-indigo-900/35 text-purple-200 hover:from-purple-900/55 hover:to-indigo-900/55'
                        : 'border-purple-200 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 text-purple-700 hover:from-purple-500/20 hover:to-indigo-500/20'
                    }`}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <IoCreateOutline className="text-2xl" />
                    {t('homeTab.textPolish')}
                  </motion.button>
                </section>

                <section className={`rounded-[28px] border p-4 ${
                  isDarkMode ? 'border-white/8 bg-[#07111f]/82' : 'border-slate-200/90 bg-white/88'
                }`}>
                  <button
                    onClick={handleShowMeme}
                    className="flex w-full items-center justify-between gap-4 text-left"
                  >
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-[0.28em] ${
                        isDarkMode ? 'text-slate-500' : 'text-slate-400'
                      }`}>
                        {t('homeTab.lightBreak', 'Light break')}
                      </p>
                      <h3 className={`mt-1.5 text-[1.08rem] font-semibold ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}>
                        {t('homeTab.dailyMeme')}
                      </h3>
                    </div>
                    <div className="relative flex items-center gap-2">
                      {!hasSeenMeme && (
                        <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                      )}
                      <span className={`rounded-full px-3 py-1.5 text-[13px] font-medium ${
                        isDarkMode ? 'bg-white/[0.06] text-slate-300' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {showMeme ? t('homeTab.hideMeme') : t('homeTab.showMeme')}
                      </span>
                      <IoBookmark className={`text-sm ${isDarkMode ? 'text-blue-400/70' : 'text-blue-500/70'}`} />
                    </div>
                  </button>

                  {showMeme && (
                    <div className="mt-4">
                      <DailyMeme isDarkMode={isDarkMode} showMeme={showMeme} />
                    </div>
                  )}
                </section>

                <BlogSection isDarkMode={isDarkMode} />

                <section className={`relative overflow-hidden rounded-[30px] border p-4 ${
                  isDarkMode
                    ? 'border-fuchsia-400/10 bg-[linear-gradient(160deg,rgba(4,12,26,0.94),rgba(20,15,43,0.92))] shadow-[0_24px_80px_rgba(4,8,20,0.35)]'
                    : 'border-fuchsia-100 bg-[linear-gradient(160deg,rgba(255,255,255,0.96),rgba(250,245,255,0.94))] shadow-[0_20px_60px_rgba(192,132,252,0.12)]'
                }`}>
                  <div className={`absolute inset-0 ${
                    isDarkMode
                      ? 'bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.18),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.12),transparent_30%)]'
                      : 'bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.10),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.08),transparent_30%)]'
                  }`} />
                  <div className="relative">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-[0.28em] ${
                          isDarkMode ? 'text-fuchsia-200/70' : 'text-fuchsia-700/80'
                        }`}>
                          {t('homeTab.creativeCorner', 'Creative corner')}
                        </p>
                        <h2 className={`mt-1.5 max-w-[18ch] font-serif text-[1.18rem] font-semibold leading-snug tracking-tight md:text-[1.24rem] ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          {t('homeTab.poemFeatureTitle', 'A poem from your reading trail')}
                        </h2>
                      </div>
                      <div className="flex flex-col items-center gap-1.5">
                        <div className={`rounded-full p-2 ${
                          isDarkMode ? 'bg-fuchsia-400/10 text-fuchsia-200' : 'bg-fuchsia-50 text-fuchsia-600'
                        }`}>
                          <IoSparkles size={16} />
                        </div>
                        <span className={`text-[9px] font-medium tracking-wider uppercase ${
                          isDarkMode ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          AI
                        </span>
                      </div>
                    </div>
                    {memoizedGeneratedPoem}
                  </div>
                </section>

              </div>
            </div>
          </aside>

          <main className="min-h-0 overflow-hidden">
            <div className={`homepage-scrollbar homepage-scroll-fade flex h-full min-h-0 flex-col gap-4 overflow-y-auto pr-2 ${
              isAuthenticated ? 'pb-8' : 'pb-28'
            }`}>
              <RecommendationSection
                recentPdfs={recentPdfs}
                isDarkMode={isDarkMode}
              />

              <RecentPDFs
                sortedPdfs={sortedPdfs}
                onOpenPdf={onOpenPdf}
                onRemoveFromRecent={onRemoveFromRecent}
                onAddPdf={onAddPdf}
                isDarkMode={isDarkMode}
              />

              <KeywordSubscriptions
                isDarkMode={isDarkMode}
                keywords={keywords}
                showSubscriptionInput={showSubscriptionInput}
                toggleSubscriptionInput={toggleSubscriptionInput}
                newKeyword={newKeyword}
                setNewKeyword={setNewKeyword}
                handleAddKeyword={handleAddKeyword}
                handleRemoveKeyword={handleRemoveKeyword}
                handleKeywordClick={handleKeywordClick}
                selectedKeyword={selectedKeyword}
                displayedKeywords={displayedKeywords}
                isLoading={isLoading}
                displayedPdfs={displayedPdfs}
                handlePubMedClick={handlePubMedClick}
                showAllPapers={showAllPapers}
                toggleShowAllPapers={toggleShowAllPapers}
                loadingKeywords={loadingKeywords}
                handleUpdateAll={updateAllKeywords}
              />
            </div>
          </main>
        </div>
      </div>

      <AnimatePresence>
        {showTokenPurchase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <TokenPurchase 
              isDarkMode={isDarkMode} 
              onClose={handleTokenPurchaseClose} 
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showReferralModal && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <ReferralModal
              isOpen={showReferralModal}
              onClose={() => setShowReferralModal(false)}
              isDarkMode={isDarkMode}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showPolishModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <TextPolishModal
              isDarkMode={isDarkMode}
              onClose={handlePolishModalClose}
              forceTokenUpdate={tokenDisplayKey}
              onTokenUpdate={setTokenDisplayKey}
              isAuthenticated={isAuthenticated}
              onLogin={onLogin}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {!isAuthenticated && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className={`absolute inset-x-3 bottom-3 z-30 overflow-hidden rounded-[24px] px-4 py-3 shadow-[0_18px_60px_rgba(2,6,23,0.26)] md:px-5 ${
            isDarkMode
              ? 'bg-[linear-gradient(90deg,#a21caf_0%,#8b5cf6_48%,#60a5fa_100%)]'
              : 'bg-[linear-gradient(90deg,#d946ef_0%,#a855f7_48%,#60a5fa_100%)]'
          }`}
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/75">
                {t('homeTab.unlockPersonalization', 'Unlock personalization')}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-white">
                {t('loginModal.welcome')}
              </h2>
              <p className="mt-1 text-sm text-white/88">
                {t('loginModal.description')}
              </p>
            </div>

            <motion.button
              onClick={onLogin}
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('homeTab.loginSignUp')}
            </motion.button>
          </div>
        </motion.section>
      )}
      
      {/* Version display */}
      <div className={`absolute right-5 ${isAuthenticated ? 'bottom-4' : 'bottom-24'}`}>
        <p className={`text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'} opacity-70`}>
          v{packageJson.version}
        </p>
      </div>
    </div>
  );
});

export default HomeTab;
